import { Colors, Lightning, Router, Storage } from "@lightningjs/sdk";

import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../components/input/Button/Button";
import theme from "../../lib/theme";
import { STORAGE_KEYS } from "../../lib/utils";

const BOX_W = 640;
const BOX_H = 320;

interface ParentalReminderTemplateSpec extends Lightning.Component.TemplateSpec {
    Box: {
        Background: object;
        Content: {
            Label: typeof TranslatableText;
            Actions: {
                Later: typeof Button;
                Settings: typeof Button;
            };
        };
    };
}

// Sky youth-protection spec: remind the customer every 6 months (per device)
// to review their parental control settings.
export class ParentalReminder
    extends Lightning.Component<ParentalReminderTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<ParentalReminderTemplateSpec>
{
    readonly Box = this.getByRef("Box")!;
    readonly Content = this.Box.getByRef("Content")!;
    readonly Actions = this.Content.getByRef("Actions")!;
    readonly Later = this.Actions.getByRef("Later")!;
    readonly Settings = this.Actions.getByRef("Settings")!;

    static override _template(): Lightning.Component.Template<ParentalReminderTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,
            x: 0,
            y: 0,
            rect: true,
            color: Colors(theme.colors.bg.overlay).alpha(0.7).get(),
            alpha: 0,
            zIndex: 40,
            collision: true,
            Box: {
                collision: true,
                w: BOX_W,
                h: BOX_H,
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                Background: {
                    x: -1,
                    y: -1,
                    texture: Lightning.Tools.getRoundRect(
                        BOX_W,
                        BOX_H,
                        12,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.secondary).get()
                    )
                },
                Content: {
                    collision: true,
                    w: (w) => w,
                    h: (h) => h,
                    mount: 0.5,
                    x: (w) => w / 2,
                    y: (h) => h / 2,
                    flex: {
                        direction: "column",
                        alignItems: "center",
                        justifyContent: "center"
                    },
                    flexItem: { grow: 1 },
                    Label: {
                        type: TranslatableText,
                        key: "parentalReminder.title",
                        text: {
                            fontSize: 28,
                            textAlign: "center",
                            wordWrap: true,
                            wordWrapWidth: BOX_W - 80
                        }
                    },
                    Actions: {
                        collision: true,
                        flexItem: { marginTop: 36 },
                        w: 560,
                        h: 56,
                        flex: {
                            direction: "row",
                            alignItems: "flex-start",
                            justifyContent: "space-between"
                        },
                        Later: {
                            type: Button,
                            zIndex: 50,
                            label: "parentalReminder.later",
                            w: 220,
                            signals: {
                                handleClick: "_handleLaterClick"
                            }
                        },
                        Settings: {
                            type: Button,
                            zIndex: 50,
                            label: "parentalReminder.settings",
                            w: 300,
                            signals: {
                                handleClick: "_handleSettingsClick"
                            }
                        }
                    }
                }
            }
        };
    }

    open() {
        // Stamp on show, not on dismiss: the reminder was displayed either way.
        Storage.set(STORAGE_KEYS.parentalReminderAt, String(Date.now()));
        this._setState("LaterState");
        Router.focusWidget("ParentalReminder");
    }

    _close() {
        Router.focusPage();
    }

    _openSettings() {
        Router.focusPage();
        Router.navigate("settings");
    }

    _handleLaterClick() {
        this._close();
    }

    _handleSettingsClick() {
        this._openSettings();
    }

    override _getFocused(): any {
        return this;
    }

    override _focus() {
        this.patch({
            smooth: {
                alpha: 1
            }
        });
    }

    override _unfocus() {
        this.patch({
            smooth: {
                alpha: 0
            }
        });
    }

    override _handleUp() {
        // keep focus inside the dialog
    }

    override _handleDown() {
        // keep focus inside the dialog
    }

    override _handleBack() {
        this._close();
    }

    static override _states() {
        return [
            class LaterState extends this {
                override _getFocused() {
                    return this.Later;
                }

                override _handleEnter() {
                    this._close();
                }

                override _handleRight() {
                    this._setState("SettingsState");
                }
            },
            class SettingsState extends this {
                override _getFocused() {
                    return this.Settings;
                }

                override _handleEnter() {
                    this._openSettings();
                }

                override _handleLeft() {
                    this._setState("LaterState");
                }
            }
        ];
    }
}
